import { useNavigate } from 'react-router-dom';
import { Badge, Button } from '@insio/ui-toolkit';
import type { AiEntity, AiEntityPartType } from 'juneau';
import { navigation } from '@/navigation';
import { isValidEntityId } from './entity-part.types';
import { RESULT_CARD_CLASS, ResultCardHeader } from '../ResultCard';
import { useValidationPathLabel } from './use-validation-path-label';
import type { BadgeColor } from '../badge-colors';

interface Props {
  part: AiEntityPartType;
}

/** Maps juneau badge tones to UI Toolkit badge colors. */
const BADGE_COLOR: Record<string, BadgeColor> = {
  success: 'success',
  warning: 'warning',
  danger: 'error',
  neutral: 'neutral'
};

/** Maps known entity kinds to their detail routes. */
const ENTITY_ROUTES: Record<string, (id: string) => string> = {
  invoice: id => navigation.invoiceDetail(id),
  document: id => navigation.documentDetail(id),
  receipt: id => navigation.receiptDetail(id)
};

function entityRoute(entityType: string | undefined, id: unknown): string | null {
  if (!entityType || !isValidEntityId(id)) return null;
  const route = ENTITY_ROUTES[entityType];
  return route ? route(id) : null;
}

export function EntityCardPart({ part }: Props) {
  const navigate = useNavigate();
  const { entity, entityType } = part;
  const route = entityRoute(entityType, entity.id);

  const rawSubtitle = entity.subtitle;
  const labelForPath = useValidationPathLabel();
  const translatedPath = labelForPath(entityType === 'validation-issue' ? rawSubtitle : undefined);
  const subtitle =
    entityType === 'validation-issue' ? (translatedPath ?? rawSubtitle) : rawSubtitle;

  const body = (
    <>
      <ResultCardHeader title={entity.title} />
      {subtitle && <span className='text-foreground-muted text-sm'>{subtitle}</span>}
      {entity.fields && entity.fields.length > 0 && (
        <dl className='flex flex-col gap-2xs m-0'>
          {entity.fields.map((field, i) => (
            <div key={i} className='flex items-baseline justify-between gap-sm'>
              <dt className='text-foreground-muted text-sm'>{field.label}</dt>
              <dd className='m-0 text-foreground-base text-sm text-right'>
                {field.badge ? (
                  <Badge color={BADGE_COLOR[field.badge] ?? 'neutral'} size='sm'>
                    {field.value}
                  </Badge>
                ) : (
                  field.value
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </>
  );

  if (route) {
    return (
      <Button
        variant='ghost'
        // The toolkit Button centres its label in a nowrap span; the card body is a
        // full-width column, so the span is unwrapped back into the flow here.
        className={`${RESULT_CARD_CLASS} h-auto justify-start text-left transition-colors hover:bg-background-muted [&>span]:flex [&>span]:w-full [&>span]:flex-col [&>span]:gap-sm [&>span]:whitespace-normal [&>span]:text-left`}
        onClick={() => navigate(route)}
      >
        {body}
      </Button>
    );
  }

  return <div className={RESULT_CARD_CLASS}>{body}</div>;
}

interface ListProps {
  part: { entities: AiEntity[]; title?: string; entityType?: string };
}

export function EntityListPart({ part }: ListProps) {
  return (
    <div className='flex flex-col gap-sm my-sm'>
      {part.title && (
        <span className='font-semibold text-foreground-muted text-sm'>{part.title}</span>
      )}
      {part.entities.map((entity, i) => (
        <EntityCardPart
          key={entity.id ?? i}
          part={{ type: 'entity', entity, entityType: part.entityType }}
        />
      ))}
    </div>
  );
}
